/**
 * REST client for command-service.
 *
 * - Injects `Authorization: Bearer <jwt>` if a token getter is registered.
 * - On 401, calls the registered refresher exactly once and replays the request.
 * - The auth store registers the token getter + refresher at app boot via
 *   `setAuthHelpers()` to avoid a circular dependency between this file and
 *   the Svelte store.
 */

import { PUBLIC_COMMAND_SERVICE_URL } from '$env/static/public';

const COMMAND_SERVICE_URL = PUBLIC_COMMAND_SERVICE_URL || 'https://api.tickedge.ai';

let getToken: (() => string | null) | null = null;
let refreshToken: (() => Promise<string | null>) | null = null;

export function setAuthHelpers(
  tokenGetter: () => string | null,
  refresher: () => Promise<string | null>
) {
  getToken = tokenGetter;
  refreshToken = refresher;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string
  ) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** If true, do NOT attach Authorization header. Use for /v1/auth/* endpoints. */
  anonymous?: boolean;
  /** Internal: prevents infinite refresh loops. */
  _retried?: boolean;
}

export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, anonymous, _retried, headers, ...rest } = options;

  const url = path.startsWith('http')
    ? path
    : `${COMMAND_SERVICE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers as Record<string, string> | undefined)
  };

  if (!anonymous) {
    const token = getToken?.();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  if (res.status === 401 && !anonymous && !_retried && refreshToken) {
    const newToken = await refreshToken();
    if (newToken) {
      return api<T>(path, { ...options, _retried: true });
    }
  }

  // 204 No Content has no body to parse.
  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const parsed: unknown = contentType.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    throw new ApiError(res.status, parsed);
  }

  return parsed as T;
}

export const apiBaseUrl = COMMAND_SERVICE_URL;
